import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  getMyReservationsApi,
  cancelReservationApi,
  getReservationRescheduleOptionsApi,
  rescheduleReservationApi,
  getMyOpenMatchesApi,
  leaveOpenMatchApi,
  cancelOpenMatchApi,
} from "../../api/api";
import RIcon from "./components/ReservasIcons";
import OpenMatchHistoryCard from "./components/OpenMatchHistoryCard";
import ReviewFormModal from "./components/ReviewFormModal";
import RescheduleModal from "./components/RescheduleModal";
import { deadlineLabel, reasonMessage, remainingLabel, unwrap } from "./components/rescheduleModel";
import { BEAUTY_FALLBACK_IMAGE } from "./components/ReservaShared";
import { isBeautyReservation, isCourtReservation } from "./reservationOutcome";
import { cartTotals, formatDuration, formatMoney } from "../beauty/beautyBooking";
import { resumenCompacto } from "../../helpers/beautyLineas";
import { QRCode } from "antd";
import { SPORT_LABELS, DURATION_LABELS, perPlayerPrice } from "../../constants/courtDomain";
import "../myevents/myevents.css";
import "./reservas.css";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=800&q=80";

// Beauty rows fall back to a salon photo; Mesas/Canchas keep the bar one.
const fallbackFor = (r) => (isBeautyReservation(r) ? BEAUTY_FALLBACK_IMAGE : FALLBACK_IMAGE);

const STATUS_MAP = {
  pending: { cls: "upcoming", label: "Pendiente" },
  reserved: { cls: "upcoming", label: "Próxima" },
  confirmed: { cls: "upcoming", label: "Confirmada" },
  cancelled: { cls: "cancel", label: "Cancelada" },
  expired: { cls: "cancel", label: "Expirada" },
  no_show: { cls: "past", label: "No Show" },
};

const statusCls = (status) => STATUS_MAP[status]?.cls || "past";
const statusLabel = (status) => STATUS_MAP[status]?.label || status || "-";

// Court reservations (Unit 4, task 4.1/4.2 — additive only, bar branch
// below is untouched). `/api/app/reservation/mine` returns both bar and
// club reservations from the same TableReservation collection; club
// bookings always carry `durationMinutes>0` and a non-empty `sport`
// (see backend `helper/payments/reservationCheckoutBuilder.js`
// `buildPendingCourtReservation`), while bars leave both fields at their
// schema defaults (`durationMinutes:0`, `sport:""`). No `businessType`
// field is populated on the `mine` payload, so this is the reliable
// signal (design.md sec.6/risk#7 verified at apply).
// Beauty rows also carry durationMinutes>0: `isCourtReservation` (imported
// from reservationOutcome) excludes them explicitly via `kind === "beauty"`.
const beautyServices = (r) => (Array.isArray(r.services) ? r.services : []);
const beautyServicesLabel = (r) => resumenCompacto(beautyServices(r));
const beautyDuration = (r) => Number(r.durationMinutes) || cartTotals(beautyServices(r)).durationMinutes;

const isUpcoming = (r) => ["pending", "reserved", "confirmed"].includes(r.status) && !dayjs(r.date).isBefore(dayjs(), "day");
const isPast = (r) => ["no_show"].includes(r.status) || (["pending", "reserved", "confirmed"].includes(r.status) && dayjs(r.date).isBefore(dayjs(), "day"));
const isCancel = (r) => ["cancelled", "expired"].includes(r.status);

const TABS = [
  { key: "todas", label: "Todas", filter: () => true },
  { key: "upcoming", label: "Próximas", filter: isUpcoming },
  { key: "past", label: "Pasadas", filter: isPast },
  { key: "cancel", label: "Canceladas", filter: isCancel },
];

// Open matches ("Falta un jugador") share the status tabs with court reservations: live matches count as
// upcoming/past by date, cancelled ones and matches the user left as cancelled.
const matchAsReservation = (m) => ({
  date: m.date,
  status: m.me?.left || m.status === "cancelled" ? "cancelled" : "confirmed",
});

// Sort key for the merged Canchas list (newest slot first, same as /open-matches/mine).
const slotKey = (x) => `${dayjs(x.date).format("YYYY-MM-DD")} ${x.slotStart || "00:00"}`;

const KIND_TABS = [
  { key: "mesas", label: "Mesas", filter: (r) => !isCourtReservation(r) && !isBeautyReservation(r) },
  { key: "canchas", label: "Canchas", filter: (r) => isCourtReservation(r) },
  { key: "beauty", label: "Beauty", filter: (r) => isBeautyReservation(r) },
];

const Historial = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth?.accessToken);

  const [reservations, setReservations] = useState([]);
  // Open matches of the logged-in player; `null` when the call failed (holds then stay visible as rows).
  const [openMatches, setOpenMatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("todas");
  // `/beauty/historial` opens straight on the Beauty tab.
  const startsOnBeauty = typeof window !== "undefined" && window.location.pathname.startsWith("/beauty");
  const [kind, setKind] = useState(startsOnBeauty ? "beauty" : "mesas");
  const [kindTouched, setKindTouched] = useState(startsOnBeauty);
  const [open, setOpen] = useState(null);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Reservation the buyer is writing a review for (opened from the card or the detail modal).
  const [reviewing, setReviewing] = useState(null);

  // The API accepted the review: reflect it locally (no reload needed) and close the form.
  const handleReviewed = (rating) => {
    const apply = (r) => (r._id === reviewing?._id ? { ...r, canReview: false, review: { rating } } : r);
    setReservations((current) => current.map(apply));
    setOpen((current) => (current ? apply(current) : current));
    setReviewing(null);
  };

  // A reschedule succeeded: merge the updated reservation into the list and the open detail, no reload.
  const handleRescheduled = (updated) => {
    if (!updated?._id) return;
    const apply = (r) => (r._id === updated._id ? { ...r, ...updated } : r);
    setReservations((current) => current.map(apply));
    setOpen((current) => (current ? apply(current) : current));
  };

  const handleCancelReservation = async () => {
    if (!open) return;
    try {
      setCancelling(true);
      setError("");
      await cancelReservationApi(open._id, token);
      await loadReservations();
      setOpen(null);
      setShowConfirmCancel(false);
    } catch (err) {
      setError(err?.response?.data?.message || "No pudimos cancelar la reserva. Intentá de nuevo.");
      setShowConfirmCancel(false);
    } finally {
      setCancelling(false);
    }
  };

  const loadReservations = useCallback(async () => {
    if (!token) {
      navigate("/login", { state: { from: "/reservas/historial" } });
      return;
    }
    try {
      setLoading(true);
      setError("");
      // The open-matches call must never break the reservations list.
      const [response, matchesResponse] = await Promise.all([
        getMyReservationsApi(token),
        Promise.resolve().then(() => getMyOpenMatchesApi(token)).catch(() => null),
      ]);
      const data = response?.data?.info;
      setReservations(Array.isArray(data) ? data : []);
      const matches = matchesResponse?.data?.info?.matches;
      setOpenMatches(Array.isArray(matches) ? matches : null);
    } catch (err) {
      setError(err?.response?.data?.message || "No pudimos cargar tu historial.");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { loadReservations(); }, [loadReservations]);

  // Default to whichever subsection actually has reservations, so a
  // court-only (or bar-only) user doesn't land on an empty "Mesas" tab.
  // Only runs once, before the user picks a tab themselves.
  useEffect(() => {
    if (kindTouched || loading || (reservations.length === 0 && !(openMatches || []).length)) return;
    const hasMesas = reservations.some((r) => !isCourtReservation(r) && !isBeautyReservation(r));
    const hasCanchas = reservations.some((r) => isCourtReservation(r)) || (openMatches || []).length > 0;
    const hasBeauty = reservations.some((r) => isBeautyReservation(r));
    if (!hasMesas && hasCanchas) setKind("canchas");
    else if (!hasMesas && hasBeauty) setKind("beauty");
  }, [loading, reservations, openMatches, kindTouched]);

  const activeFilter = TABS.find((t) => t.key === tab)?.filter || (() => true);
  const activeKindFilter = KIND_TABS.find((k) => k.key === kind)?.filter || (() => true);
  // Hold rows of an open match are replaced by the match card (no duplicate), but only when the match loaded.
  const matchIds = useMemo(() => new Set((openMatches || []).map((m) => String(m._id))), [openMatches]);
  const visibleReservations = reservations.filter((r) => !(r.openMatchId && matchIds.has(String(r.openMatchId))));
  const reservationItems = visibleReservations.filter(activeKindFilter).filter(activeFilter).map((r) => ({ type: "reservation", r }));
  const matchItems =
    kind === "canchas"
      ? (openMatches || []).filter((m) => activeFilter(matchAsReservation(m))).map((m) => ({ type: "match", m }))
      : [];
  const list = [...reservationItems, ...matchItems];
  if (matchItems.length > 0) {
    list.sort((a, b) => slotKey(b.r || b.m).localeCompare(slotKey(a.r || a.m)));
  }

  const afterMatchAction = async (action, matchId) => {
    await action(matchId, "", token);
    await loadReservations();
  };

  return (
    <div className="reservas-root">
      <div className="wrap historial">
        <button className="crumb" onClick={() => navigate("/reservas")}>
          <RIcon name="chevL" width={16} height={16} /> Volver a Reservas
        </button>

        <div className="myevents-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", marginTop: "24px", marginBottom: "8px" }}>
          <h1 className="myevents-title" style={{ fontSize: "28px", fontWeight: "700", color: "#2D3035", margin: 0 }}>Mis reservas</h1>
          <button
            className="r-btn-primary"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
            onClick={() => navigate(kind === "canchas" ? "/canchas" : kind === "beauty" ? "/beauty" : "/reservas")}
          >
            <RIcon name="plus" width={17} height={17} /> Nueva reserva
          </button>
        </div>

        <div className="hist-tabs" role="tablist" aria-label="Tipo de reserva">
          {KIND_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`hist-tab ${kind === key ? "on" : ""}`}
              onClick={() => { setKind(key); setKindTouched(true); }}
              role="tab"
              aria-selected={kind === key}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="myevents-tabs hist-filter-tabs" role="tablist" aria-label="Filtros de reservas">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`myevents-tab ${tab === key ? "is-active" : ""}`}
              onClick={() => setTab(key)}
              role="tab"
              aria-selected={tab === key}
            >
              {label}
            </button>
          ))}
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spinner animation="border" />
          </div>
        ) : list.length === 0 ? (
          <div className="empty-state">
            <RIcon name="history" width={48} height={48} />
            <h3>No tenés reservas acá</h3>
            <p>
              {kind === "canchas"
                ? "Cuando reserves una cancha la vas a encontrar en esta sección."
                : kind === "beauty"
                  ? "Todavía no tenés turnos de beauty."
                  : "Cuando reserves una mesa la vas a encontrar en esta sección."}
            </p>
            <button
              className="r-btn-primary"
              style={{ borderRadius: 12, padding: "12px 24px" }}
              onClick={() => navigate(kind === "canchas" ? "/canchas" : kind === "beauty" ? "/beauty" : "/reservas")}
            >
              {kind === "canchas" ? "Reservar una cancha" : kind === "beauty" ? "Reservar un turno" : "Reservar una mesa"}
            </button>
          </div>
        ) : (
          <div className="hist-list">
            {list.map((item) => {
              if (item.type === "match") {
                return (
                  <OpenMatchHistoryCard
                    key={`match-${item.m._id}`}
                    match={item.m}
                    onOpen={(id) => navigate(`/partidos/${id}`)}
                    onLeave={(id) => afterMatchAction(leaveOpenMatchApi, id)}
                    onCancel={(id) => afterMatchAction(cancelOpenMatchApi, id)}
                  />
                );
              }
              const r = item.r;
              const dateParsed = r.date ? dayjs(r.date) : null;
              return (
                <div className="hist-card" key={String(r._id)} onClick={() => setOpen(r)}>
                  <div className="hist-thumb">
                    <img src={r.barImage || fallbackFor(r)} alt={r.barName} onError={(e) => { e.currentTarget.src = fallbackFor(r); }} />
                    {dateParsed && (
                      <div className="hist-date-badge">
                        <div className="d">{dateParsed.format("DD")}</div>
                        <div className="m">{dateParsed.format("MMM").toUpperCase()}</div>
                      </div>
                    )}
                  </div>
                  <div className="hist-mid">
                    <div className="hn">{r.barName || (isBeautyReservation(r) ? "Salón" : "Bar")}</div>
                    <div className="hmeta">
                      {r.date && <span className="hm-left"><RIcon name="cal" width={14} height={14} /> {dayjs(r.date).format("DD/MM/YYYY")}</span>}
                      {r.slotStart && <span className="hm-right"><RIcon name="clock" width={14} height={14} /> {r.slotStart} hs</span>}
                      {r.zone && <span className="hm-left"><RIcon name="pin" width={14} height={14} /> {r.zone}</span>}
                      {isBeautyReservation(r) && r.professionalName && <span className="hm-left"><RIcon name="user" width={14} height={14} /> {r.professionalName}</span>}
                      {isBeautyReservation(r) && beautyServicesLabel(r) && <span className="hm-right"><RIcon name="spark" width={14} height={14} /> {beautyServicesLabel(r)}{beautyDuration(r) > 0 ? ` · ${formatDuration(beautyDuration(r))}` : ""}</span>}
                      {!isCourtReservation(r) && !isBeautyReservation(r) && r.partySize && <span className="hm-right"><RIcon name="users" width={14} height={14} /> {r.partySize} personas</span>}
                      {isCourtReservation(r) && r.tableLabel && <span className="hm-left"><RIcon name="court" width={14} height={14} /> {r.tableLabel}</span>}
                      {isCourtReservation(r) && r.sport && <span className="hm-right"><RIcon name="ball" width={14} height={14} /> {SPORT_LABELS[r.sport] || r.sport}</span>}
                      {isCourtReservation(r) && r.durationMinutes > 0 && <span className="hm-left"><RIcon name="clock" width={14} height={14} /> {DURATION_LABELS[r.durationMinutes] || `${r.durationMinutes} min`}</span>}
                    </div>
                  </div>
                  <div className="hist-side">
                    <span className={`status-pill ${statusCls(r.status)}`}>
                      <i />{statusLabel(r.status)}
                    </span>
                    {isBeautyReservation(r) ? (
                      (Number(r.paidamount) > 0 || r.depositAmount > 0) && (
                        <span className="hprice">{formatMoney(Number(r.paidamount) > 0 ? r.paidamount : r.depositAmount)}</span>
                      )
                    ) : r.depositAmount > 0 && (
                      <span className="hprice">${Number(r.depositAmount).toLocaleString("es-AR")}</span>
                    )}
                    {r.canReview && (
                      <button
                        type="button"
                        className="r-btn-soft"
                        style={{ padding: "6px 12px", borderRadius: 9, fontSize: 12 }}
                        onClick={(e) => { e.stopPropagation(); setReviewing(r); }}
                      >
                        Dejar reseña
                      </button>
                    )}
                    {r.review && (
                      <span className="rv-own" data-testid="own-review">
                        <span aria-label={`Tu reseña: ${r.review.rating} de 5`}>★ {r.review.rating}</span>
                      </span>
                    )}
                    <span className="harrow">
                      Ver detalle <RIcon name="chevR" width={15} height={15} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {open && (
          <HistModal
            r={open}
            token={token}
            onClose={() => setOpen(null)}
            navigate={navigate}
            onCancel={() => setShowConfirmCancel(true)}
            onReview={() => setReviewing(open)}
            onRescheduled={handleRescheduled}
          />
        )}

        {reviewing && (
          <ReviewFormModal reservation={reviewing} token={token} onClose={() => setReviewing(null)} onSubmitted={handleReviewed} />
        )}

        {showConfirmCancel && (
          <ConfirmCancelModal
            onConfirm={handleCancelReservation}
            onCancel={() => setShowConfirmCancel(false)}
            loading={cancelling}
            depositAmount={open?.depositAmount}
            status={open?.status}
          />
        )}
      </div>
    </div>
  );
};

// Beauty turnos that are still live and start in the future can be moved.
const isReschedulable = (r) => {
  if (!isBeautyReservation(r) || !["reserved", "confirmed"].includes(r.status) || !r.date) return false;
  const start = dayjs(`${dayjs(r.date).format("YYYY-MM-DD")}T${r.slotStart || "00:00"}`);
  return start.isValid() && start.isAfter(dayjs());
};

function HistModal({ r, token, onClose, navigate, onCancel, onReview, onRescheduled }) {
  const isUp = isUpcoming(r);
  const showReschedule = isReschedulable(r);
  const [rescheduleInfo, setRescheduleInfo] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);

  // Ask the back whether this turno can still be moved (limit / deadline) to show the right state.
  const rescheduleKey = `${r._id}|${r.date}|${r.slotStart}|${r.rescheduleCount ?? ""}`;
  useEffect(() => {
    if (!showReschedule) return undefined;
    let alive = true;
    (async () => {
      try {
        const info = unwrap(await getReservationRescheduleOptionsApi(r._id, dayjs().format("YYYY-MM-DD"), token));
        if (alive) setRescheduleInfo(info);
      } catch (e) {
        if (alive) setRescheduleInfo(null);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescheduleKey, showReschedule, token]);
  const rescheduleBlocked = rescheduleInfo?.allowed === false;
  const rescheduleHints = [remainingLabel(rescheduleInfo?.remaining), deadlineLabel(rescheduleInfo?.deadline)].filter(Boolean);
  const canCancel = isUp && r.cancellationAllowed && r.cancellationAllowed !== "false";
  const policyHours = Number(r.cancellationPolicyHours ?? 24);
  const slotTime = dayjs(`${r.date} ${r.slotStart}`, "YYYY-MM-DD HH:mm");
  const hoursLeft = slotTime.isValid() ? slotTime.diff(dayjs(), "hour", true) : -1;
  const insidePolicyWindow = hoursLeft >= policyHours;
  // Court routing (Unit 4, task 4.1/4.2): route the detail link to
  // `/canchas/:clubId` instead of `/reservas/:barId`. The `mine` payload
  // only populates `barId` as a bare ObjectId (no slug), so `_id` is the
  // only usable identifier here (verified at apply — see design.md risk#7).
  // Falls through to the exact prior bar path/label when isCourtReservation
  // is false, so bar behavior is byte-identical.
  const isCourt = isCourtReservation(r);
  const isBeauty = isBeautyReservation(r);
  const detailPath = isBeauty ? `/beauty/${r.barId}` : isCourt ? `/canchas/${r.barId}` : `/reservas/${r.barId}`;
  const detailLabel = isBeauty ? "Ver local" : isCourt ? "Ver cancha" : "Ver bar";
  const bookAgainPath = isBeauty ? `/beauty/${r.barId}` : isCourt ? "/canchas" : "/reservas";

  return (
    <div className="r-modal-back" onClick={onClose}>
      <div className="r-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hero">
          <img
            src={r.barImage || fallbackFor(r)}
            alt={r.barName}
            onError={(e) => { e.currentTarget.src = fallbackFor(r); }}
          />
          <div className="sc" />
          <button className="x" onClick={onClose}>
            <RIcon name="x" width={18} height={18} />
          </button>
          <div className="mh-name">
            <div className="nm">{r.barName || (isBeauty ? "Salón" : "Bar")}</div>
            {r.zone && (
              <div className="lc">
                <RIcon name="pin" width={14} height={14} /> {r.zone}
              </div>
            )}
            {isCourt && r.tableLabel && (
              <div className="lc">
                <RIcon name="court" width={14} height={14} /> {r.tableLabel}
              </div>
            )}
          </div>
        </div>
        <div className="modal-body">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <span className={`status-pill ${statusCls(r.status)}`}>
              <i />{statusLabel(r.status)}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#6b7280" }}>
              Código <strong style={{ color: "#1a1d20" }}>{r.reservationCode}</strong>
            </span>
          </div>
          <div className="modal-grid">
            {r.date && (
              <div className="mg">
                <div className="l"><RIcon name="cal" width={14} height={14} /> Fecha</div>
                <div className="v">{dayjs(r.date).format("DD/MM/YYYY")}</div>
              </div>
            )}
            {r.slotStart && (
              <div className="mg">
                <div className="l"><RIcon name="clock" width={14} height={14} /> Horario</div>
                <div className="v">{r.slotStart}{r.slotEnd ? ` – ${r.slotEnd}` : ""} hs</div>
              </div>
            )}
            {r.zone && (
              <div className="mg">
                <div className="l"><RIcon name="pin" width={14} height={14} /> Zona</div>
                <div className="v">{r.zone}</div>
              </div>
            )}
            {isBeauty && r.professionalName && (
              <div className="mg">
                <div className="l"><RIcon name="user" width={14} height={14} /> Profesional</div>
                <div className="v">{r.professionalName}</div>
              </div>
            )}
            {isBeauty && beautyServicesLabel(r) && (
              <div className="mg">
                <div className="l"><RIcon name="spark" width={14} height={14} /> Servicios</div>
                <div className="v">{beautyServicesLabel(r)}</div>
              </div>
            )}
            {isBeauty && beautyDuration(r) > 0 && (
              <div className="mg">
                <div className="l"><RIcon name="clock" width={14} height={14} /> Duración</div>
                <div className="v">{formatDuration(beautyDuration(r))}</div>
              </div>
            )}
            {!isCourt && !isBeauty && r.partySize && (
              <div className="mg">
                <div className="l"><RIcon name="users" width={14} height={14} /> Comensales</div>
                <div className="v">{r.partySize} personas</div>
              </div>
            )}
            {isCourt && r.sport && (
              <div className="mg">
                <div className="l"><RIcon name="ball" width={14} height={14} /> Deporte</div>
                <div className="v">{SPORT_LABELS[r.sport] || r.sport}</div>
              </div>
            )}
            {isCourt && r.durationMinutes > 0 && (
              <div className="mg">
                <div className="l"><RIcon name="clock" width={14} height={14} /> Duración</div>
                <div className="v">{DURATION_LABELS[r.durationMinutes] || `${r.durationMinutes} min`}</div>
              </div>
            )}
            {isCourt && r.partySize > 0 && (
              <div className="mg">
                <div className="l"><RIcon name="users" width={14} height={14} /> Jugadores</div>
                <div className="v">{r.partySize} {r.partySize === 1 ? "jugador" : "jugadores"}</div>
              </div>
            )}
            {isCourt && perPlayerPrice(r.totalPrice, r.partySize) !== null && (
              <div className="mg">
                <div className="l"><RIcon name="ticket" width={14} height={14} /> Cada uno paga</div>
                <div className="v">${perPlayerPrice(r.totalPrice, r.partySize).toLocaleString("es-AR")}</div>
              </div>
            )}
          </div>
          {isBeauty && r.depositAmount > 0 && (
            <div className="modal-total">
              <span className="l">Seña</span>
              <span className="v">{formatMoney(r.depositAmount)}</span>
            </div>
          )}
          {isBeauty && Number(r.paidamount) > 0 && (
            <div className="modal-total">
              <span className="l">Pagado</span>
              <span className="v">{formatMoney(r.paidamount)}</span>
            </div>
          )}
          {!isBeauty && r.depositAmount > 0 && (
            <div className="modal-total">
              <span className="l">Seña abonada</span>
              <span className="v">${Number(r.depositAmount).toLocaleString("es-AR")}</span>
            </div>
          )}
          {(r.refundStatus === "pending_organizer" ||
            (r.status === "cancelled" && r.paymentstatus === "paid" && r.depositAmount > 0)) && (
            <div style={{
              backgroundColor: "#f1f5f9",
              borderRadius: 10,
              padding: "12px 14px",
              marginTop: 14,
              display: "flex",
              gap: 10,
              alignItems: "flex-start"
            }}>
              <span style={{ color: "#2563eb", flexShrink: 0, marginTop: 1 }}>
                <RIcon name="info" width={18} height={18} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#334155", lineHeight: 1.5 }}>
                Reembolso de la seña (${Number(r.depositAmount).toLocaleString("es-AR")}) a coordinar con el organizador. Te contactará a la brevedad.
              </span>
            </div>
          )}
          {r.qrToken && (r.status === "confirmed" || r.status === "reserved") && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              marginTop: 22,
              paddingTop: 22,
              marginBottom: 22,
              borderTop: "1.5px dashed #e2e8f0",
              gap: 10
            }}>
              <QRCode value={r.qrToken} size={150} bordered={false} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: "0.03em", textTransform: "uppercase", textAlign: "center" }}>
                Presentá este código QR al llegar al establecimiento
              </span>
            </div>
          )}
          {showReschedule && (
            <div className="rs-box">
              <button
                type="button"
                className="r-btn-soft"
                style={{ padding: "10px 16px", borderRadius: 10 }}
                disabled={rescheduleBlocked}
                onClick={() => setRescheduling(true)}
              >
                Reagendar
              </button>
              {rescheduleBlocked ? (
                <span className="rs-box-note" role="status">{reasonMessage(rescheduleInfo.reason)}</span>
              ) : rescheduleHints.length > 0 ? (
                <span className="rs-box-note">{rescheduleHints.join(" · ")}</span>
              ) : null}
            </div>
          )}
          {rescheduling && (
            <RescheduleModal
              title="Reagendar turno"
              initialDate={dayjs(r.date).format("YYYY-MM-DD")}
              summary={[r.barName || "Salón", beautyServicesLabel(r), `${dayjs(r.date).format("DD/MM/YYYY")} ${r.slotStart || ""} hs`].filter(Boolean).join(" · ")}
              loadOptions={(date) => getReservationRescheduleOptionsApi(r._id, date, token)}
              onConfirm={({ date, slotStart }) => rescheduleReservationApi(r._id, { date, slotStart }, token)}
              onClose={() => setRescheduling(false)}
              onDone={(res) => { setRescheduling(false); onRescheduled?.(unwrap(res)); }}
            />
          )}
          {r.review ? (
            <div className="rv-review-block">
              <span className="rv-own">Tu reseña: ★ {r.review.rating}</span>
            </div>
          ) : r.canReview ? (
            <div className="rv-review-block">
              <span style={{ fontSize: 13, fontWeight: 600, color: "#4b5563" }}>¿Cómo fue tu experiencia?</span>
              <button type="button" className="r-btn-soft" style={{ padding: "8px 14px", borderRadius: 10 }} onClick={onReview}>
                Dejar reseña
              </button>
            </div>
          ) : null}
          <div className="modal-foot" style={r.review || r.canReview ? { marginTop: 18 } : undefined}>
            {isUp ? (
              <>
                <button className="r-btn-soft" style={{ flex: 1, padding: 13, borderRadius: 11 }} onClick={onClose}>
                  Cerrar
                </button>
                {canCancel && insidePolicyWindow && (
                  <button
                    className="r-btn-danger"
                    style={{ flex: 1, padding: 13, borderRadius: 11, backgroundColor: "#ef4444", color: "#fff", border: "none" }}
                    onClick={() => onCancel(r._id)}
                  >
                    Cancelar reserva
                  </button>
                )}
                {(!canCancel || !insidePolicyWindow) && (
                  <button
                    className="r-btn-dark"
                    style={{ flex: 1, padding: 13, borderRadius: 11 }}
                    onClick={() => { onClose(); navigate(detailPath); }}
                  >
                    {detailLabel}
                  </button>
                )}
              </>
            ) : (
              <button
                className="r-btn-dark"
                style={{ flex: 1, padding: 13, borderRadius: 11 }}
                onClick={() => { onClose(); navigate(bookAgainPath); }}
              >
                Reservar de nuevo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmCancelModal({ onConfirm, onCancel, loading, depositAmount, status }) {
  const hasDeposit = Number(depositAmount) > 0 && status === "confirmed";
  return (
    <div className="r-modal-back" style={{ zIndex: 9999 }} onClick={onCancel}>
      <div className="r-modal" style={{ maxWidth: 400, padding: 24, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#fdecea",
          color: "#ef4444",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px"
        }}>
          <RIcon name="info" width={28} height={28} />
        </div>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: "#1a1d20", marginBottom: 8 }}>¿Cancelar reserva?</h3>
        <p style={{ fontSize: 14, fontWeight: 500, color: "#4b5563", lineHeight: 1.5, marginBottom: hasDeposit ? 16 : 24 }}>
          ¿Estás seguro de que querés cancelar esta reserva? Esta acción no se puede deshacer.
        </p>
        {hasDeposit && (
          <div style={{
            backgroundColor: "#f1f5f9",
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: 24,
            textAlign: "left",
            display: "flex",
            gap: 10,
            alignItems: "flex-start"
          }}>
            <span style={{ color: "#2563eb", flexShrink: 0, marginTop: 1 }}>
              <RIcon name="info" width={18} height={18} />
            </span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#334155", lineHeight: 1.5 }}>
              Pagaste una seña de <strong>${Number(depositAmount).toLocaleString("es-AR")}</strong>. Al cancelar, el organizador se pondrá en contacto con vos para coordinar el reembolso.
            </span>
          </div>
        )}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            className="r-btn-soft"
            style={{ flex: 1, padding: 12, borderRadius: 10 }}
            onClick={onCancel}
            disabled={loading}
          >
            No, conservar
          </button>
          <button
            className="r-btn-danger"
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 10,
              backgroundColor: "#ef4444",
              color: "#fff",
              border: "none",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8
            }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Cancelando...
              </>
            ) : (
              "Sí, cancelar"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Historial;
